<h1
  mat-dialog-title
  class="products-dialog-title">
  Select Products for {{company?.name || data.companyName}}
</h1>
<div mat-dialog-content class="products-dialog-content">
  <div class="setting-mode-panel">
    <div class="setting-mode-buttons">
      @if (canInherit()) {
        <button
          mat-stroked-button
          type="button"
          [class.active-mode]="mode === 'inherit'"
          (click)="setMode('inherit')">
          Use Parent Setting
        </button>
      }
      <button
        mat-stroked-button
        type="button"
        [class.active-mode]="mode === 'none'"
        (click)="setMode('none')">
        No Products For This Company
      </button>
      <button
        mat-stroked-button
        type="button"
        [class.active-mode]="mode === 'custom'"
        (click)="setMode('custom')">
        Set Custom Products
      </button>
    </div>
    @if (mode === 'inherit' && canInherit()) {
      <p class="setting-mode-help">
        This company will use the Available Products from {{data.parentCompanyName || 'its parent company'}}.
      </p>
    }
    @if (mode === 'none') {
      <p class="setting-mode-help">
        This company will explicitly override the parent setting and have no available products.
      </p>
    }
    @if (mode === 'custom' && !data.showPayrollDeductionColumn) {
      <p class="setting-mode-help">
        Select the products that should be available for this company.
      </p>
    }
    @if (mode === 'custom' && data.showPayrollDeductionColumn) {
      <p class="setting-mode-help">
        Select the products that should be available for this company, and which ones can be mapped to payroll deduction.
      </p>
    }
  </div>
  @if (hasProductImportOptions()) {
    <div class="brand-products-helper" [class.collapsed]="!isBrandProductHelperExpanded">
      <button
        type="button"
        class="brand-products-helper-header"
        [attr.aria-expanded]="isBrandProductHelperExpanded"
        (click)="toggleBrandProductHelper()">
        <span class="brand-products-helper-title">Select products from existing brand or company</span>
        <span class="brand-products-helper-action">{{isBrandProductHelperExpanded ? 'Hide' : 'Show'}}</span>
      </button>
      @if (isBrandProductHelperExpanded) {
        <div class="brand-products-helper-body">
          <div class="brand-products-helper-controls">
            <mat-form-field appearance="outline">
              <mat-label>Source type</mat-label>
              <mat-select
                [(ngModel)]="productImportSourceType"
                [disabled]="isBrandProductResolutionLoading"
                (ngModelChange)="onProductImportSourceTypeChange($event)">
                @if (data.brandOptions.length > 0) {
                  <mat-option value="brand">Existing brand</mat-option>
                }
                @if (data.companyOptions.length > 0) {
                  <mat-option value="company">Existing company</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>{{productImportSourceType === 'company' ? 'Company product source' : 'Brand product source'}}</mat-label>
              @if (productImportSourceType === 'brand') {
                <mat-select
                  [(ngModel)]="selectedBrandProductBrandId"
                  [disabled]="isBrandProductResolutionLoading">
                  @for (brand of data.brandOptions; track brand) {
                    <mat-option [value]="brand.id">
                      {{brand.label}}
                      @if (brand.subtitle) {
                        <span>({{brand.subtitle}})</span>
                      }
                    </mat-option>
                  }
                </mat-select>
              }
              @if (productImportSourceType === 'company') {
                <mat-select
                  [(ngModel)]="selectedCompanyProductCompanyId"
                  [disabled]="isBrandProductResolutionLoading">
                  @for (companyOption of data.companyOptions; track companyOption) {
                    <mat-option [value]="companyOption.id">
                      {{companyOption.label}}
                      @if (companyOption.subtitle) {
                        <span>({{companyOption.subtitle}})</span>
                      }
                    </mat-option>
                  }
                </mat-select>
              }
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Apply behavior</mat-label>
              <mat-select
                [(ngModel)]="productImportApplyMode"
                [disabled]="isBrandProductResolutionLoading"
                (ngModelChange)="setProductImportApplyMode($event)">
                <mat-option value="replace">Replace current selection</mat-option>
                <mat-option value="merge">Merge with current selection</mat-option>
              </mat-select>
            </mat-form-field>
            <button
              mat-stroked-button
              type="button"
              color="primary"
              [disabled]="(productImportSourceType === 'brand' ? !selectedBrandProductBrandId : !selectedCompanyProductCompanyId) || isBrandProductResolutionLoading"
              (click)="loadProductImportPreview()">
              Preview Products
            </button>
            @if (isBrandProductResolutionLoading) {
              <mat-spinner [diameter]="24"></mat-spinner>
            }
          </div>
          @if (brandProductResolutionError) {
            <app-inlinemessage
              level="warning"
              [message]="brandProductResolutionError">
            </app-inlinemessage>
          }
          @if (productImportPreviewLoaded && !brandProductResolutionError) {
            <div class="brand-products-preview">
              @if (hasBrandProductNoMatchState()) {
                <app-inlinemessage
                  level="warning"
                    [message]="productImportSourceType === 'brand'
                        ? 'No mapped vendor products were found for this brand. The current selection was not changed.'
                        : 'No selectable products were found for this company. The current selection was not changed.'">
                </app-inlinemessage>
              }
              @if (hasBrandProductPartialMatchState()) {
                <app-inlinemessage
                  level="warning"
                  message="Some products for this brand could not be matched to a vendor product. Review skipped products before applying.">
                </app-inlinemessage>
              }
              @if (companyProductPreviewUnavailableProductIds.length > 0) {
                <app-inlinemessage
                  level="warning"
                  message="Some products from this company are no longer available in the product catalog and will be skipped.">
                </app-inlinemessage>
              }
              <p class="brand-products-summary">
                {{brandProductPreviewProductIds.length}} selectable products from {{productImportSourceLabel}}.
                {{brandProductPreviewAddedProductIds.length}} would be added, {{brandProductPreviewOverlappingProductIds.length}} are already selected, and {{brandProductPreviewRemovedProductIds.length}} would be removed.
              </p>
              @if (productImportSourceType === 'company') {
                <p class="brand-products-summary">
                  Category, Subcategory, Requires Approval, Payroll Deduction, and Always Available settings will also be copied for these products.
                  Source-company settings take precedence for products that are already selected.
                </p>
              }
              <div class="brand-products-diff">
                <div>
                  <h3>Would Add</h3>
                  @if (brandProductPreviewAddedProductIds.length === 0) {
                    <p>No products</p>
                  }
                  @if (brandProductPreviewAddedProductIds.length > 0) {
                    <ul>
                      @for (productId of brandProductPreviewAddedProductIds; track productId) {
                        <li>{{getProductDisplay(productId)}}</li>
                      }
                    </ul>
                  }
                </div>
                <div>
                  <h3>Already Selected</h3>
                  @if (brandProductPreviewOverlappingProductIds.length === 0) {
                    <p>No products</p>
                  }
                  @if (brandProductPreviewOverlappingProductIds.length > 0) {
                    <ul>
                      @for (productId of brandProductPreviewOverlappingProductIds; track productId) {
                        <li>{{getProductDisplay(productId)}}</li>
                      }
                    </ul>
                  }
                </div>
                <div>
                  <h3>Would Remove</h3>
                  @if (brandProductPreviewRemovedProductIds.length === 0) {
                    <p>No products</p>
                  }
                  @if (brandProductPreviewRemovedProductIds.length > 0) {
                    <ul>
                      @for (productId of brandProductPreviewRemovedProductIds; track productId) {
                        <li>{{getProductDisplay(productId)}}</li>
                      }
                    </ul>
                  }
                </div>
                @if (brandProductPreviewSkippedProducts.length > 0) {
                  <div>
                    <h3>Skipped</h3>
                    <ul>
                      @for (product of brandProductPreviewSkippedProducts; track product) {
                        <li>
                          {{getSkippedStripeProductDisplay(product)}} - {{getSkippedStripeProductReason(product)}}
                        </li>
                      }
                    </ul>
                  </div>
                }
                @if (companyProductPreviewUnavailableProductIds.length > 0) {
                  <div>
                    <h3>Skipped</h3>
                    <ul>
                      @for (productId of companyProductPreviewUnavailableProductIds; track productId) {
                        <li>
                          {{productId}} - Not found in the current product catalog
                        </li>
                      }
                    </ul>
                  </div>
                }
              </div>
              <button
                mat-raised-button
                type="button"
                color="accent"
                [disabled]="!canApplyBrandProductPreview()"
                (click)="applyBrandProductPreview()">
                Apply To Custom Selection
              </button>
              @if (brandProductResolutionApplied) {
                <p class="brand-products-applied">
                  Products were applied to the dialog selection. Use Done to save.
                </p>
              }
            </div>
          }
        </div>
      }
    </div>
  }
  @if (items.data.length > 0) {
    <div class="products-table-toolbar">
      <div class="products-table-toolbar-primary">
        <mat-form-field appearance="outline" class="products-search-field">
          <mat-label>Search products</mat-label>
          <input
            matInput
            type="search"
            placeholder="Vendor, product, or product ID"
            [(ngModel)]="productSearch"
            (ngModelChange)="setProductSearch($event)">
          @if (productSearch) {
            <button
              mat-icon-button
              matSuffix
              type="button"
              aria-label="Clear product search"
              (click)="clearProductSearch()">
              <mat-icon>close</mat-icon>
            </button>
          }
        </mat-form-field>
      </div>
      <mat-checkbox
        class="selected-only-control"
        [checked]="showSelectedProductsOnly"
        (change)="setShowSelectedProductsOnly($event.checked)">
        Show selected only ({{getSelectedProductCount()}})
      </mat-checkbox>
    </div>
  }
  <div class="products-dialog-messages">
    @if (items.data.length === 0) {
      <app-inlinemessage level="warning" message="No products available"></app-inlinemessage>
    }
    @if (items.data.length > 0 && showSelectedProductsOnly && getSelectedProductCount() === 0) {
      <app-inlinemessage
        level="warning"
        message="No selected products to show">
      </app-inlinemessage>
    }
    @if (items.data.length > 0 && !!productSearch && items.filteredData.length === 0) {
      <app-inlinemessage
        level="warning"
        message="No products match this search">
      </app-inlinemessage>
    }
  </div>
  <div class="products-table-shell">
    <table class="table products-table" mat-table matSort matSortActive="vendorName" matSortDirection="asc" [dataSource]="items">
      <ng-container matColumnDef="generalProductsGroup">
        <th
          mat-header-cell
          *matHeaderCellDef
          class="products-group-header-cell general-products-group"
          [attr.colspan]="4">
        </th>
      </ng-container>
      <ng-container matColumnDef="payrollAdminGroup">
        <th
          mat-header-cell
          *matHeaderCellDef
          class="products-group-header-cell payroll-admin-group"
          [attr.colspan]="getPayrollAdminColumnCount()">
          <span class="payroll-admin-group-label">
            <mat-icon aria-hidden="true">admin_panel_settings</mat-icon>
            Payroll Admin
          </span>
        </th>
      </ng-container>
      <ng-container matColumnDef="select">
        <th mat-header-cell *matHeaderCellDef>
          <div class="column-header select-column-header">
            <mat-checkbox #ref (change)="$event ? masterToggle(ref) : null"
              aria-label="Select or clear all available products"
              [checked]="selection.hasValue() && isAllSelected() && isSomeSelected()"
              [indeterminate]="selection.hasValue() && !isSomeSelected() && !isAllSelected()"
              [disabled]="!isCustomMode()">
            </mat-checkbox>
            <button
              type="button"
              class="column-help-button"
              [matTooltip]="productColumnHelpText.select"
              [attr.aria-label]="'Row selection help: ' + productColumnHelpText.select"
              (click)="$event.stopPropagation()">
              <mat-icon aria-hidden="true">help_outline</mat-icon>
            </button>
          </div>
        </th>
        <td mat-cell *matCellDef="let row" >
          <mat-checkbox
            (click)="$event.stopPropagation()"
            (change)="$event ? toggleRow(row) : null"
            [attr.aria-label]="'Select ' + getProductDisplay(row.id)"
            [disabled]="!isCustomMode()"
            [checked]="selection.isSelected(row)">
          </mat-checkbox>
        </td>
      </ng-container>
      <ng-container matColumnDef="vendorName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header matTooltip="Sort Ascending/Descending/None">Vendor</th>
        <td class="cell" mat-cell *matCellDef="let item">{{item.vendorName}}</td>
      </ng-container>
      <ng-container matColumnDef="alwaysActive">
        <th mat-header-cell *matHeaderCellDef>
          <div class="column-header">
            <span>Always Available</span>
            <button
              type="button"
              class="column-help-button"
              [matTooltip]="productColumnHelpText.alwaysActive"
              [attr.aria-label]="'Always Available help: ' + productColumnHelpText.alwaysActive"
              (click)="$event.stopPropagation()">
              <mat-icon aria-hidden="true">help_outline</mat-icon>
            </button>
          </div>
        </th>
        <td mat-cell *matCellDef="let row">
          <mat-checkbox
            (click)="$event.stopPropagation()"
            (change)="$event ? toggleAlwaysActiveRow(row) : null"
            [attr.aria-label]="'Always Available for ' + getProductDisplay(row.id)"
            [disabled]="!isCustomMode() || !isAlwaysActiveProductAvailable(row.id)"
            [checked]="alwaysActiveSelection.isSelected(row)">
          </mat-checkbox>
        </td>
      </ng-container>
      <ng-container matColumnDef="payrollBenefitCategory">
        <th mat-header-cell *matHeaderCellDef>
          <div class="column-header">
            <span>Category</span>
            <button
              type="button"
              class="column-help-button"
              [matTooltip]="productColumnHelpText.payrollBenefitCategory"
              [attr.aria-label]="'Category help: ' + productColumnHelpText.payrollBenefitCategory"
              (click)="$event.stopPropagation()">
              <mat-icon aria-hidden="true">help_outline</mat-icon>
            </button>
          </div>
        </th>
        <td mat-cell *matCellDef="let row">
          @if (canEditProductPayrollBenefitCategory(row.id)) {
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="payroll-benefit-field">
              <mat-select
                [value]="getProductPayrollBenefitCategory(row.id)"
                (selectionChange)="setProductPayrollBenefitCategory(row.id, $event.value)">
                <mat-option value="">None</mat-option>
                @for (category of payrollBenefitCategoryOptions; track category) {
                  <mat-option [value]="category">
                    {{getPayrollBenefitCategoryLabel(category)}}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          } @else {
            <span class="payroll-benefit-readonly">{{getProductPayrollBenefitCategoryLabel(row.id)}}</span>
          }
        </td>
      </ng-container>
      <ng-container matColumnDef="payrollBenefitSubcategory">
        <th mat-header-cell *matHeaderCellDef>
          <div class="column-header">
            <span>Subcategory</span>
            <button
              type="button"
              class="column-help-button"
              [matTooltip]="productColumnHelpText.payrollBenefitSubcategory"
              [attr.aria-label]="'Subcategory help: ' + productColumnHelpText.payrollBenefitSubcategory"
              (click)="$event.stopPropagation()">
              <mat-icon aria-hidden="true">help_outline</mat-icon>
            </button>
          </div>
        </th>
        <td mat-cell *matCellDef="let row">
          @if (canEditProductPayrollBenefitCategory(row.id) && hasPayrollBenefitSubcategoryOptions(getProductPayrollBenefitCategory(row.id))) {
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="payroll-benefit-field">
              <mat-select
                [value]="getProductPayrollBenefitSubcategory(row.id)"
                (selectionChange)="setProductPayrollBenefitSubcategory(row.id, $event.value)">
                <mat-option value="">None</mat-option>
                @for (subcategory of getPayrollBenefitSubcategoryOptions(getProductPayrollBenefitCategory(row.id)); track subcategory) {
                  <mat-option
                    [value]="subcategory">
                    {{getPayrollBenefitSubcategoryLabel(getProductPayrollBenefitCategory(row.id), subcategory)}}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          } @else {
            <span class="payroll-benefit-readonly">{{getProductPayrollBenefitSubcategoryLabel(row.id)}}</span>
          }
        </td>
      </ng-container>
      <ng-container matColumnDef="requiresApproval">
        <th mat-header-cell *matHeaderCellDef>
          <div class="column-header">
            <span>Requires Approval</span>
            <button
              type="button"
              class="column-help-button"
              [matTooltip]="productColumnHelpText.requiresApproval"
              [attr.aria-label]="'Requires Approval help: ' + productColumnHelpText.requiresApproval"
              (click)="$event.stopPropagation()">
              <mat-icon aria-hidden="true">help_outline</mat-icon>
            </button>
          </div>
        </th>
        <td mat-cell *matCellDef="let row">
          <mat-checkbox
            (click)="$event.stopPropagation()"
            (change)="$event ? toggleRequiresApprovalRow(row) : null"
            [attr.aria-label]="'Requires Approval for ' + getProductDisplay(row.id)"
            [disabled]="!isCustomMode() || !isRequiresApprovalProductAvailable(row.id)"
            [checked]="requiresApprovalSelection.isSelected(row)">
          </mat-checkbox>
        </td>
      </ng-container>
      <ng-container matColumnDef="payrollDeduction">
        <th mat-header-cell *matHeaderCellDef>
          <div class="column-header">
            <span>Payroll Deduction</span>
            <button
              type="button"
              class="column-help-button"
              [matTooltip]="productColumnHelpText.payrollDeduction"
              [attr.aria-label]="'Payroll Deduction help: ' + productColumnHelpText.payrollDeduction"
              (click)="$event.stopPropagation()">
              <mat-icon aria-hidden="true">help_outline</mat-icon>
            </button>
          </div>
        </th>
        <td mat-cell *matCellDef="let row">
          <mat-checkbox
            (click)="$event.stopPropagation()"
            (change)="$event ? togglePayrollDeductionRow(row) : null"
            [attr.aria-label]="'Payroll Deduction for ' + getProductDisplay(row.id)"
            [disabled]="!isCustomMode() || !isPayrollDeductionProductAvailable(row.id)"
            [checked]="payrollDeductionSelection.isSelected(row)">
          </mat-checkbox>
        </td>
      </ng-container>
      <ng-container matColumnDef="allowanceControl">
        <th mat-header-cell *matHeaderCellDef>
          <div class="column-header">
            <span>Allowance Control</span>
            <button
              type="button"
              class="column-help-button"
              [matTooltip]="productColumnHelpText.allowanceControl"
              [attr.aria-label]="'Allowance Control help: ' + productColumnHelpText.allowanceControl"
              (click)="$event.stopPropagation()">
              <mat-icon aria-hidden="true">help_outline</mat-icon>
            </button>
          </div>
        </th>
        <td mat-cell *matCellDef="let row">
          <mat-checkbox
            (click)="$event.stopPropagation()"
            (change)="$event ? toggleAllowanceControlRow(row) : null"
            [attr.aria-label]="'Allowance Control for ' + getProductDisplay(row.id)"
            [disabled]="!isCustomMode() || !isAllowanceControlProductAvailable(row.id)"
            [checked]="allowanceControlSelection.isSelected(row)">
          </mat-checkbox>
        </td>
      </ng-container>
      <ng-container matColumnDef="productName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header matTooltip="Sort Ascending/Descending/None">Product</th>
        <td class="cell" mat-cell *matCellDef="let item">{{item.productName}}</td>
      </ng-container>
      <ng-container matColumnDef="vendorSlug">
        <th mat-header-cell *matHeaderCellDef mat-sort-header matTooltip="Sort Ascending/Descending/None">Product ID</th>
        <td class="cell" mat-cell *matCellDef="let item">{{item.vendorSlug}}-{{item.productSlug}}</td>
      </ng-container>
      <tr class="products-group-header-row" mat-header-row *matHeaderRowDef="productGroupHeaderColumns"></tr>
      <tr class="products-column-header-row" mat-header-row *matHeaderRowDef="allColumnLabels"></tr>
      <tr class="listitem" mat-row *matRowDef="let rows; columns: allColumnLabels"></tr>
    </table>
  </div>
</div>
<div class="buttons" mat-dialog-actions>
  <button mat-raised-button class="donebutton" color="primary" (click)="okClicked()">Done</button>
  @if (items.data.length > 0) {
    <button mat-button (click)="onDismiss()">Cancel</button>
  }
</div>
